/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import { FaBox, FaShoppingCart, FaChartLine } from 'react-icons/fa';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    BarChart,
    Bar,
    Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL;

const SupplierDashboard = () => {
    const { user } = useContext(UserContext);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [supplierOrders, setSupplierOrders] = useState([]);
    const [ordersByDate, setOrdersByDate] = useState([]);
    const [ordersByStatus, setOrdersByStatus] = useState([]);
    const [topSellingProducts, setTopSellingProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const accessToken = localStorage.getItem('accessToken');

    useEffect(() => {
        if (user && user.userType === 'supplier') {
            fetchSupplierData();
        } else {
            setLoading(false);
            setError("Accès refusé. Vous n'êtes pas un fournisseur.");
        }
    }, [user]);

    const fetchSupplierData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/dashboard-data`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            if (!response.ok) {
                throw new Error('Échec de la récupération des données du tableau de bord.');
            }

            const data = await response.json();

            // Format product data for the chart
            const formattedProducts = data.products.map(p => ({
                ...p,
                stockQuantity: Number(p.stockQuantity) || 0
            }));

            setSupplierProducts(formattedProducts);
            setSupplierOrders(data.orders);

            // FIX: Format order data for the evolution chart
            const ordersFormattedForChart = data.orders.reduce((acc, order) => {
                // Ensure date is consistent for grouping, then format for display
                const dateKey = new Date(order.createdAt).toISOString().split('T')[0]; // YYYY-MM-DD
                const displayDate = new Date(order.createdAt).toLocaleDateString('fr-FR'); // For display
                if (!acc[dateKey]) {
                    acc[dateKey] = { date: displayDate, sortKey: dateKey, orders: 0 };
                }
                acc[dateKey].orders++;
                return acc;
            }, {});

            setOrdersByDate(Object.values(ordersFormattedForChart).sort((a, b) => new Date(a.sortKey) - new Date(b.sortKey)));

            // FIX: Data for Orders by Status Pie Chart
            const statusData = data.orders.reduce((acc, order) => {
                const status = order.status;
                const displayName = statusMap[status] || status;
                const existingStatus = acc.find(item => item.name === displayName);
                if (existingStatus) {
                    existingStatus.value++;
                } else {
                    acc.push({ name: displayName, value: 1 });
                }
                return acc;
            }, []);
            setOrdersByStatus(statusData);

            // FIX: Data for Top Selling Products Bar Chart
            const productSales = data.orders.reduce((acc, order) => {
                // Assuming your order data has productId, quantity, and productName
                // Note: The previous logic seemed to assume order has productName, which is unusual for an order item.
                // Assuming it's coming from a simplified/pre-aggregated API.
                const orderItems = Array.isArray(order.items) ? order.items : [{ productName: order.productName, quantity: order.quantity }];

                orderItems.forEach(item => {
                    const existingProduct = acc.find(p => p.productName === item.productName);
                    if (existingProduct) {
                        existingProduct.quantitySold += item.quantity;
                    } else {
                        acc.push({ productName: item.productName, quantitySold: item.quantity });
                    }
                });
                return acc;
            }, []);

            const topProducts = productSales.sort((a, b) => b.quantitySold - a.quantitySold).slice(0, 5);
            setTopSellingProducts(topProducts);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };


    const pieColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#f44336'];

    if (loading) return <div className="p-8 text-center text-gray-500">Chargement du tableau de bord...</div>;
    if (error) return <div className="p-8 text-center text-red-500 font-semibold">{error}</div>;

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8"> {/* Added responsive padding */}
            <header className="mb-6 lg:mb-8">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900"> {/* Responsive font size */}
                    Tableau de Bord du Fournisseur 📦
                </h1>
                <p className="mt-2 text-base sm:text-lg text-gray-500">
                    Bienvenue, <span className="text-indigo-400 font-medium">{user.firstName}</span>! Gérez et suivez vos produits et commandes.
                </p>
            </header>

            {/* Metric Cards: Responsive grid for 1, 2, then 3 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 lg:mb-8">
                <div className="bg-white p-5 rounded-xl shadow-lg flex items-center space-x-4 transition duration-300 hover:shadow-xl">
                    <FaBox className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-500" />
                    <div>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-700">Total Produits</h2>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
                            {supplierProducts.length}
                        </p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-lg flex items-center space-x-4 transition duration-300 hover:shadow-xl">
                    <FaShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />
                    <div>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-700">Commandes en cours</h2>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
                            {supplierOrders.filter(o => o.status === 'in_transit').length}
                        </p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-lg flex items-center space-x-4 transition duration-300 hover:shadow-xl">
                    <FaChartLine className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500" />
                    <div>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-700">Total des commandes</h2>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
                            {supplierOrders.length}
                        </p>
                    </div>
                </div>
            </div>

            {/* Charts: Responsive grid for 1 column, then 2 columns */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6 lg:mb-8">

                {/* Top Selling Products Chart */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Top 5 des produits les plus vendus</h2>
                    {/* ResponsiveContainer takes care of chart responsiveness */}
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={topSellingProducts} margin={{ top: 5, right: 30, left: 0, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="productName"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                                // Adjusted font size for better mobile display
                                style={{ fontSize: '10px' }}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend wrapperStyle={{ paddingTop: '10px' }} />
                            <Bar dataKey="quantitySold" fill="#82ca9d" name="Quantité vendue" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Orders by Status Chart */}
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Commandes par statut</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={ordersByStatus}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                fill="#8884d8"
                                // Use custom label for better responsiveness on smaller screens if needed
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            >
                                {ordersByStatus.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [value, name]} />
                            {/* Legend is crucial for mobile */}
                            <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: '10px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Orders Evolution Chart */}
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-6 lg:mb-8">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Évolution des commandes</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={ordersByDate} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        {/* Use a smaller font size for XAxis on smaller screens */}
                        <XAxis dataKey="date" style={{ fontSize: '10px' }} />
                        <YAxis />
                        <Tooltip />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                        <Line type="monotone" dataKey="orders" stroke="#3b82f6" name="Nombre de commandes" strokeWidth={2} activeDot={{ r: 8 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>


            {/* List of My Products */}
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-8">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Mes Produits</h2>
                {/* Ensure table is responsive with overflow-x-auto */}
                <div className="overflow-x-auto">
                    <table className="min-w-full table-auto">
                        <thead>
                            <tr className="bg-gray-50 text-gray-600 uppercase text-xs sm:text-sm font-semibold">
                                {/* Adjusted padding and text size for better mobile fit */}
                                <th className="py-3 px-4 sm:px-6 text-left rounded-tl-xl">Produit</th>
                                <th className="py-3 px-4 sm:px-6 text-left hidden sm:table-cell">Description</th> {/* Hide description on small screens */}
                                <th className="py-3 px-4 sm:px-6 text-right">Prix</th>
                                <th className="py-3 px-4 sm:px-6 text-right rounded-tr-xl">Stock</th> {/* Simplified header text */}
                            </tr>
                        </thead>
                        <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                            {supplierProducts.length > 0 ? (
                                supplierProducts.map((product) => (
                                    <tr key={product.productId} className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="py-3 px-4 sm:px-6 text-left font-medium text-xs sm:text-sm">{product.productName}</td>
                                        {/* Hide description on small screens */}
                                        <td className="py-3 px-4 sm:px-6 text-left text-xs sm:text-sm hidden sm:table-cell">
                                            {product.description ? `${product.description.substring(0, 40)}...` : 'N/A'}
                                        </td>
                                        <td className="py-3 px-4 sm:px-6 text-right text-xs sm:text-sm">{product.price} $</td>
                                        <td className="py-3 px-4 sm:px-6 text-right text-xs sm:text-sm font-semibold">{product.stockQuantity}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="py-10 text-center text-gray-500">
                                        Aucun produit n'est associé à votre compte.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SupplierDashboard;